import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { resetPasswordSchema, type ResetPasswordInput } from '@/lib/schemas';
import { useResetPassword } from '@/hooks/use-auth';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function ResetPasswordPage() {
    const { t } = useI18n();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';
    const resetPassword = useResetPassword();
    const [complete, setComplete] = useState(false);
    const { register, handleSubmit, formState: { errors } } = useForm<ResetPasswordInput>({
        resolver: zodResolver(resetPasswordSchema),
        defaultValues: { newPassword: '', confirmPassword: '' },
    });

    const onSubmit = async (data: ResetPasswordInput) => {
        if (!token) return;
        try {
            await resetPassword.mutateAsync({ ...data, token });
            setComplete(true);
        } catch {
            // The mutation state renders the invalid/expired link message.
        }
    };

    if (!token) {
        return (
            <div className="flex min-h-screen items-center justify-center p-4">
                <Card className="w-full max-w-md p-6 text-center space-y-4">
                    <XCircle className="h-12 w-12 text-destructive mx-auto" />
                    <p>{t('auth.invalidResetLink')}</p>
                    <Button asChild><Link to="/forgot-password">{t('auth.forgotPassword')}</Link></Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="flex min-h-screen items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <CardTitle>{t('auth.resetPassword')}</CardTitle>
                    <CardDescription>{t('auth.resetPasswordDescription')}</CardDescription>
                </CardHeader>
                {complete ? (
                    <>
                        <CardContent className="text-center space-y-4">
                            <CheckCircle2 className="h-12 w-12 text-status-success mx-auto" />
                            <p className="text-sm text-foreground-secondary">{t('auth.passwordResetSuccess')}</p>
                        </CardContent>
                        <CardFooter>
                            <Button asChild className="w-full"><Link to="/login">{t('auth.signIn')}</Link></Button>
                        </CardFooter>
                    </>
                ) : (
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <CardContent className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
                                <Input id="new-password" type="password" autoComplete="new-password" {...register('newPassword')} />
                                {errors.newPassword && <p className="text-sm text-destructive">{errors.newPassword.message}</p>}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="confirm-password">{t('auth.confirmNewPassword')}</Label>
                                <Input id="confirm-password" type="password" autoComplete="new-password" {...register('confirmPassword')} />
                                {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>}
                            </div>
                            {resetPassword.isError && (
                                <p className="text-sm text-destructive">{t('auth.invalidResetLink')}</p>
                            )}
                        </CardContent>
                        <CardFooter>
                            <Button type="submit" className="w-full" disabled={resetPassword.isPending}>
                                {resetPassword.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {resetPassword.isPending ? t('auth.resettingPassword') : t('auth.resetPassword')}
                            </Button>
                        </CardFooter>
                    </form>
                )}
            </Card>
        </div>
    );
}
